import React, { useEffect, useState } from 'react'
import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { APIError } from 'helpers/api'
import { useApplicationStore } from 'hooks/application'
import useProject from 'hooks/useProject'
import CodeMirror from '@uiw/react-codemirror'
import { python } from '@codemirror/lang-python'
import { EditorView } from '@codemirror/view'
import { foldGutter, indentUnit } from '@codemirror/language'
import { updateColumn } from 'components/spreadsheet/contexts/data/actions'
import { tabAcceptsCompletion } from 'helpers/codemirror'
import Button from 'components/button'
import { Copy } from 'components/icons'
interface ScriptProps {
  columnPublicId: string
  script: string | undefined | null
  handleUpdateColumnError: (error: any) => void
}

const Script: React.FC<ScriptProps> = ({ columnPublicId, script, handleUpdateColumnError }) => {
  const { project } = useProject()
  const { spreadsheetData, setSpreadsheetData } = useDataContext()
  const { setSnackbarMessage } = useApplicationStore()

  const [newScript, setNewScript] = useState<string>('')
  const [scriptError, setScriptError] = useState<string>('')

  useEffect(() => {
    if (script) setNewScript(script)
  }, [script])

  const extensions = [python(), foldGutter(), indentUnit.of('    '), EditorView.lineWrapping, tabAcceptsCompletion]

  const handleScriptChange = () => {
    if (columnPublicId) {
      updateColumn(
        project.publicId,
        'script',
        spreadsheetData,
        setSpreadsheetData,
        columnPublicId,
        newScript,
        () => {
          setSnackbarMessage({ status: 'success', message: 'The column was updated successfully!' })
          setScriptError('')
        },
        (error) => {
          if (error instanceof APIError) {
            if (error.rawCode === 422) {
              handleUpdateColumnError(
                'Your script has an error. See the details above the script editor, fix it, then select Update Script.'
              )
              setScriptError(error.message)
            } else {
              handleUpdateColumnError(error)
            }
          } else {
            handleUpdateColumnError(error)
          }
        },
        {
          runScriptOnAllCells: true
        }
      )
    }
  }

  return (
    <div>
      <div className="mb-5">
        {scriptError !== '' && (
          <pre className="text-sm w-full bg-surface-sunken select-auto whitespace-pre-wrap mb-5 p-2.5 leading-loose break-words break-normal border-2 border-[var(--color-light-red)]">
            <div
              className="ml-auto cursor-pointer float-right"
              title="Copy"
              onClick={() => {
                navigator.clipboard.writeText(scriptError)
                setSnackbarMessage({ status: 'success', message: 'Copied!' })
              }}
            >
              <Copy />
            </div>
            {scriptError}
          </pre>
        )}

        <CodeMirror
          value={script ? script : ''}
          onChange={(value) => setNewScript(value)}
          extensions={extensions}
          theme="dark"
          basicSetup={{
            lineNumbers: true,
            foldGutter: true
          }}
        />

        <div className="flex mt-2.5">
          <Button className="ml-auto" internalType="accept" onClick={() => handleScriptChange()}>
            Update Script
          </Button>
        </div>
      </div>
    </div>
  )
}

export default Script
